import type { ReelSummary, Vod } from '../api/types';
import { isStudioJobInProgress } from '../config';
import { isGameEligibleVod, isReelEligibleVod } from './vodEligibility';

export type NextVodEmptyKind = 'none' | 'too_short' | 'caught_up';

export type NextStudioVod = {
  vod: Vod;
  runningReel: ReelSummary | null;
};

function createdMs(iso: string): number {
  const ms = Date.parse(iso);
  return Number.isNaN(ms) ? 0 : ms;
}

/** Newest eligible VOD that does not already have a delivered reel. */
export function pickNextStudioVod(
  vods: Vod[],
  reels: ReelSummary[]
): NextStudioVod | null {
  const deliveredIds = new Set(
    reels
      .filter((reel) => reel.status === 'delivered' && reel.provider_vod_id)
      .map((reel) => reel.provider_vod_id)
  );
  const eligible = vods
    .filter(
      (vod) =>
        isGameEligibleVod(vod) &&
        isReelEligibleVod(vod) &&
        !deliveredIds.has(vod.vod_id)
    )
    .sort((a, b) => createdMs(b.created_at) - createdMs(a.created_at));
  const vod = eligible[0];
  if (!vod) return null;
  const runningReel =
    reels.find(
      (reel) =>
        reel.provider_vod_id === vod.vod_id && isStudioJobInProgress(reel.status)
    ) ?? null;
  return { vod, runningReel };
}

export function nextVodEmptyKind(
  vods: Vod[],
  reels: ReelSummary[]
): NextVodEmptyKind | null {
  if (vods.length === 0) return 'none';
  const visible = vods.filter((vod) => isGameEligibleVod(vod));
  if (visible.length === 0) return 'none';
  if (!visible.some((vod) => isReelEligibleVod(vod))) return 'too_short';
  if (!pickNextStudioVod(vods, reels)) return 'caught_up';
  return null;
}

export function recentReels(reels: ReelSummary[], limit = 3): ReelSummary[] {
  return [...reels]
    .sort((a, b) => createdMs(b.created_at) - createdMs(a.created_at))
    .slice(0, limit);
}

export type ReelFilter = 'all' | 'delivered' | 'in_progress' | 'failed';

export function reelMatchesFilter(
  reel: ReelSummary,
  filter: ReelFilter
): boolean {
  if (filter === 'all') return true;
  if (filter === 'delivered') return reel.status === 'delivered';
  if (filter === 'failed') return reel.status === 'failed';
  return isStudioJobInProgress(reel.status);
}

export function reelFilterCounts(reels: ReelSummary[]): Record<ReelFilter, number> {
  return {
    all: reels.length,
    delivered: reels.filter((reel) => reel.status === 'delivered').length,
    in_progress: reels.filter((reel) => isStudioJobInProgress(reel.status)).length,
    failed: reels.filter((reel) => reel.status === 'failed').length,
  };
}
